import type { AutomaticCarouselOpts } from '@/components/Cms/AutomaticCarousel';
import { AutomaticCarousel } from '@/components/Cms/AutomaticCarousel';
import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import type { RegisterWatchBannerOpts } from '@/components/RegisterWatchBanner';
import { RegisterWatchBanner } from '@/components/RegisterWatchBanner';
import * as RegisterWatchMessages from '@/components/RegisterWatchBanner/messages';
import type {
  ContentfulPlaceholderFragment,
  ContentfulPlaceholderModelStoryFragment,
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulSliderProductsAutomaticFragment,
} from '@/graphql/contentful/api';
import type {
  CmsExtraDataRecord,
  ContentfulData,
  ContentType,
  PageSectionsOpts,
  PlaceholderType,
} from '@/integrations/contentful';
import {
  getOptForType,
  getOptsForType,
  renderContentfulSection,
} from '@/integrations/contentful';

import type {
  KeyFeaturesOpts,
  ModelStoryOps,
  ProductDetailSectionOpts,
  TechnicalDataOpts,
  UGCOpts,
  WarrantyOpts,
} from './components';
import {
  KeyFeaturesSection,
  ModelStory,
  ProductDetailSection,
  TechnicalData,
  UGC,
  Warranty,
} from './components';

export type WatchSectionsOpts = PageSectionsOpts<
  | KeyFeaturesOpts
  | TechnicalDataOpts
  | ModelStoryOps
  | ProductDetailSectionOpts
  | UGCOpts
  | WarrantyOpts
  | AutomaticCarouselOpts
  | Omit<RegisterWatchBannerOpts, 'children'>
>;

export const renderContentfulWatchSection: any = (
  data: ContentfulData,
  sectionOpts: WatchSectionsOpts,
  extraDataRecord: CmsExtraDataRecord,
  draftMode: boolean,
) => {
  const section = renderContentfulSection(
    data,
    sectionOpts,
    extraDataRecord,
    draftMode,
  );

  if (section) {
    return section;
  }

  let props = null;
  let opts = null;
  const typename = data?.__typename as ContentType;

  const productData = (sectionOpts?.[0]?.opts as any) || {};
  const defaultImageAlt = [
    productData.variantBasicDetails?.product?.name as string,
    productData.variantBasicDetails?.sku,
  ]
    .filter(Boolean)
    .join(' - ');

  switch (typename) {
    case 'PlaceholderProductDetails':
      props = data as ContentfulPlaceholderProductDetailsFragment;
      opts = getOptForType<ProductDetailSectionOpts>(
        'PlaceholderProductDetails',
        sectionOpts,
      );
      return opts ? (
        <ProductDetailSection
          data-test-id='RenderContentfulWatchSectionProductDetailSection'
          {...props}
          {...opts}
        />
      ) : null;

    case 'PlaceholderModelStory':
      props = data as ContentfulPlaceholderModelStoryFragment;
      opts = getOptForType<ModelStoryOps>('PlaceholderModelStory', sectionOpts);
      return opts ? (
        <ModelStory
          data-test-id='RenderContentfulWatchSectionModelStory'
          {...props}
          {...opts}
          alt={defaultImageAlt}
        />
      ) : null;

    case 'SliderProductsAutomatic':
      props = data as ContentfulSliderProductsAutomaticFragment;
      return renderAutomaticCarousels(draftMode, props, sectionOpts);

    case 'Placeholder':
      props = data as ContentfulPlaceholderFragment;
      return renderPlaceholders(props, sectionOpts);

    default:
      return null;
  }
};

const renderAutomaticCarousels = (
  draftMode: boolean,
  props: ContentfulSliderProductsAutomaticFragment,
  sectionOpts?: WatchSectionsOpts,
) => {
  let opts = null;

  if (
    props.type === SliderProductsTypeEnum.YouMayAlsoLike ||
    props.type === SliderProductsTypeEnum.CompatibleStraps ||
    props.type === SliderProductsTypeEnum.RecentlyViewed
  ) {
    opts =
      getOptsForType<AutomaticCarouselOpts>(
        'SliderProductsAutomatic',
        sectionOpts,
      ).find((opt) => opt.opts?.type === props.type)?.opts || {};

    const automaticCarouselProps = {
      ...props,
      ...opts,
    } as AutomaticCarouselOpts;

    return (
      <AutomaticCarousel
        data-test-id='RenderAutomaticCarouselsAutomaticCarousel'
        {...automaticCarouselProps}
        draftMode={draftMode}
      />
    );
  }

  return null;
};

const renderPlaceholders = (
  props: ContentfulPlaceholderFragment,
  sectionOpts?: WatchSectionsOpts,
) => {
  let opts = null;

  if (props.type === 'ugc') {
    opts = getOptsForType<UGCOpts>('Placeholder', sectionOpts).find(
      (opt) => opt.opts?.type === 'ugc',
    )?.opts;
    return opts ? (
      <UGC data-test-id='RenderPlaceholdersUGC' {...props} {...opts} />
    ) : null;
  }
  if (props.type === 'warranty') {
    opts = getOptsForType<WarrantyOpts>('Placeholder', sectionOpts).find(
      (opt) => opt.opts?.type === 'warranty',
    )?.opts;
    return opts ? (
      <Warranty
        data-test-id='RenderPlaceholdersWarranty'
        {...props}
        {...opts}
      />
    ) : null;
  }

  if (props.type === 'key features') {
    opts = getOptsForType<KeyFeaturesOpts>('Placeholder', sectionOpts).find(
      (opt) => opt.opts?.type === 'key features',
    )?.opts;
    return opts ? (
      <KeyFeaturesSection
        data-test-id='RenderPlaceholdersKeyFeaturesSection'
        {...props}
        {...opts}
      />
    ) : null;
  }

  if (props.type === 'technical data') {
    opts = getOptsForType<TechnicalDataOpts>('Placeholder', sectionOpts).find(
      (opt) => opt.opts?.type === 'technical data',
    )?.opts;
    return opts ? (
      <TechnicalData
        data-test-id='RenderPlaceholdersTechnicalData'
        {...props}
        {...opts}
      />
    ) : null;
  }

  if (props.type === 'register watch') {
    opts = getOptsForType<RegisterWatchBannerOpts>(
      'Placeholder',
      sectionOpts,
    ).find((opt) => opt.opts?.type === 'register watch')?.opts;
    return opts ? (
      <RegisterWatchBanner
        data-test-id='RenderPlaceholdersRegisterWatchBanner'
        {...props}
        {...opts}
      >
        <RegisterWatchBanner.SubHeader>
          {RegisterWatchMessages.Subheading}
        </RegisterWatchBanner.SubHeader>
        <RegisterWatchBanner.Text>
          {RegisterWatchMessages.Description}
        </RegisterWatchBanner.Text>
        <RegisterWatchBanner.RegisterLink />
      </RegisterWatchBanner>
    ) : null;
  }

  return null;
};

export const getEnhancedCmsData = (
  data: ContentfulData,
  i: number,
  items: ContentfulData[],
) => {
  if (data?.__typename === 'Placeholder') {
    const next = items[i + 1];

    if (next?.__typename === 'Placeholder') {
      /**
       * Those sections in mobile make up a whole section, this if there are not
       * proceeding after each other default section margin is added, otherwise
       * it is not.
       */
      const noMargin: PlaceholderType[] = [
        'warranty',
        'key features',
        'technical data',
      ];

      if (noMargin?.includes(next?.type as PlaceholderType)) {
        return {
          ...data,
          'data-test-id': 'GetEnhancedCmsDataNoMargin',
        };
      }
    }
  }

  return data;
};
